export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('')
}

export async function verifiedArtifactBlob(response: Response, expectedSha256: string): Promise<{blob: Blob, sha256: string}> {
  if (!response.ok) throw new Error(`Artifact download failed (${response.status})`)
  const expected = expectedSha256.trim().toLowerCase()
  const advertised = response.headers.get('x-proofrun-sha256')?.trim().toLowerCase()
  if (!/^[0-9a-f]{64}$/.test(expected)) throw new Error('Artifact metadata has an invalid SHA-256 digest')
  if (advertised !== expected) throw new Error('Artifact response digest does not match the report')
  const blob = await response.blob()
  const actual = await sha256Hex(await blob.arrayBuffer())
  if (actual !== expected) throw new Error('Artifact content failed SHA-256 verification')
  return {blob, sha256: actual}
}

function canonicalArtifacts(artifacts: any[]): any[] {
  return artifacts.map(({id, path, mediaType, bytes, sha256, caseId, journeyId, stepId, redacted}) => ({
    id, path, mediaType, bytes, sha256, caseId, journeyId, stepId, redacted: !!redacted,
  })).sort((left, right) => String(left.path).localeCompare(String(right.path)))
}

function decodeBase64(value: string): ArrayBuffer {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0)).buffer as ArrayBuffer
}

function publicKeyBytes(pem: string): ArrayBuffer {
  return decodeBase64(pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s/g, ''))
}

function normalizePem(pem: string): string {
  return pem.replace(/\r/g, '').trim()
}

export async function verifyEvidenceManifest(
  manifest: any,
  fetchKey: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> = fetch,
): Promise<{digest: string, signatureVerified: boolean, keyId?: string}> {
  if (!manifest || !Array.isArray(manifest.artifacts) || !/^[0-9a-f]{64}$/i.test(String(manifest.digest || ''))) {
    throw new Error('Evidence manifest is incomplete')
  }
  const digest = await sha256Hex(new TextEncoder().encode(JSON.stringify(canonicalArtifacts(manifest.artifacts))).buffer)
  if (digest !== String(manifest.digest).toLowerCase()) throw new Error('Evidence manifest digest is invalid')
  if (!manifest.signature) return {digest, signatureVerified: false}
  const signature = manifest.signature
  if (signature.algorithm !== 'Ed25519' || !signature.keyId || !signature.publicKey || !signature.value) {
    throw new Error('Evidence manifest signature is incomplete')
  }
  const response = await fetchKey(`/v1/evidence-signing-keys/${encodeURIComponent(signature.keyId)}`)
  if (!response.ok) throw new Error(`Evidence signing key '${signature.keyId}' is not trusted`)
  const trusted = await response.json()
  if (trusted.algorithm !== 'Ed25519' || trusted.keyId !== signature.keyId || normalizePem(trusted.publicKey) !== normalizePem(signature.publicKey)) {
    throw new Error('Evidence manifest signing key does not match the ProofRun trust anchor')
  }
  const key = await crypto.subtle.importKey('spki', publicKeyBytes(trusted.publicKey), {name: 'Ed25519'}, false, ['verify'])
  const valid = await crypto.subtle.verify(
    {name: 'Ed25519'},
    key,
    decodeBase64(signature.value),
    new TextEncoder().encode(digest),
  )
  if (!valid) throw new Error('Evidence manifest signature is invalid')
  return {digest, signatureVerified: true, keyId: signature.keyId}
}
